<template>
  <div class="skill-map-layout">
    <div class="flow-wrapper">
      <VueFlow
        :nodes="nodes"
        :edges="edges"
        class="skill-map"
        @pane-ready="onPaneReady"
        @node-click="onNodeClick"
        @pane-click="closePanel"
        :fit-view-on-init="true"
        :min-zoom="0.5"
        :max-zoom="1.5"
        >
        <Background />
      </VueFlow>
    <div class="skills-description">
      <h1>Skills & Specializations</h1>
      <h3 >My main Specializations are full-stack development with a strong foundation across front-end, back-end, and database design, and over a decade of hands-on experience building real-world applications. My strengths are in architecting maintainable systems, turning complex requirements into clean, usable interfaces, and owning projects end-to-end—from data modeling and APIs to polished UI and real-time features. I’m especially comfortable in JavaScript Framework ecosystems like MEVN (MySQL, Express, Vue, Node) and enjoy working where performance, clarity, and long-term scalability matter.</h3>
      <br><h3>I adapt quickly to new tools and technologies, whether that’s picking up a new framework, learning a different language, or stepping into an unfamiliar platform. I value understanding why things work, not just how to use them, and I’m always looking to expand my skill set as projects evolve. Technology never stands still, and that’s part of what keeps me excited about building, learning, and refining my craft.</h3>
      <br><h3>Select skills above to learn a little bit more about my experience with each.</h3>
    </div>
    </div>
    <!-- Skill Detail Panel -->
    <aside
      class="details-panel"
      :class="{ open: selectedSkill, mobile: isMobile }"
    >
      <template v-if="selectedSkill">
        <h3>{{ selectedSkill.label }}</h3>

        <p><strong>Category:</strong> {{ selectedSkill.category }}</p>
        <p><strong>Experience:</strong> {{ selectedSkill.experience }}</p>
        <div v-if="selectedSkill.projects != ''">
          <p><strong>Projects:</strong></p>
          <ul>
            <li v-for="p in selectedSkill.projects" :key="p">
              {{ p }}
            </li>
          </ul>
        </div>
      </template>
    </aside>
  </div>
</template>

<script>
import { VueFlow, useVueFlow } from '@vue-flow/core'
import { Background } from '@vue-flow/background'
import dagre from 'dagre'
import '@vue-flow/core/dist/style.css'
import skills from '../../assets/skills'

export default {
  name: 'Skills',
  components: {
    VueFlow,
    Background,
  },

  data() {
    return {
      nodes: [],
      edges: [],
      selectedSkill: null,
      fitView: null,
      isMobile: false,
    }
  },
  created() {
    this.flow = useVueFlow()
  },
  mounted() {
    this.checkScreen();
    window.addEventListener('resize', this.checkScreen);
    this.initializeGraph();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkScreen);
  },
  methods: {
    checkScreen() {
      this.isMobile = window.innerWidth <= 768;
    },
    onPaneReady({ fitView }) {
      this.fitView = fitView

      // Initial center
      this.fitView({
        padding: 0.25,
        duration: 500,
      })
    },
    initializeGraph() {
      const baseEdges = [
        { source: 'me', target: 'git' },
        { source: 'me', target: 'fe' },
        { source: 'me', target: 'api' },
        { source: 'me', target: 'cpanel' },
        { source: 'me', target: 'docker' },
        { source: 'me', target: 'postman' },
        { source: 'me', target: 'be' },
        { source: 'me', target: 'linux' },
        { source: 'me', target: 'd365' },
        { source: 'fe', target: 'fw' },
        { source: 'fe', target: 'bs' },
        { source: 'fe', target: 'scss' },
        { source: 'be', target: 'node' },
        { source: 'be', target: 'python' },
        { source: 'be', target: 'db' },
        { source: 'be', target: 'java' },
        { source: 'be', target: 'php' },
        { source: 'be', target: 'c++' },
        { source: 'node', target: 'express' },
        { source: 'node', target: 'sockets' },
        { source: 'db', target: 'mysql' },
        { source: 'db', target: 'mssql' },
        { source: 'db', target: 'mongo' },
        { source: 'fw', target: 'ember' },
        { source: 'fw', target: 'angular' },
        { source: 'fw', target: 'react' },
        { source: 'fw', target: 'vue' },
        // { source: 'd365', target: 'pa' },
        // { source: 'd365', target: 'pbi' },
      ]

      const { nodes, edges } = this.applyLayout(skills, baseEdges)

      this.nodes = nodes
      this.edges = edges

      this.$nextTick(() => {
        if (this.fitView) {
          this.fitView({
            padding: 0.2,
            duration: 500,
          })
        }
      })
    },

    applyLayout(skills, rawEdges) {
      const g = new dagre.graphlib.Graph()
      g.setDefaultEdgeLabel(() => ({}))
      g.setGraph({ rankdir: 'TB', ranksep: 90 })

      skills.forEach(s =>
        g.setNode(s.id, { width: 130, height: 70 })
      )

      rawEdges.forEach(e =>
        g.setEdge(e.source, e.target)
      )

      dagre.layout(g)

      const nodes = skills.map(skill => {
        const { x, y } = g.node(skill.id)
        return {
          id: skill.id,
          label: skill.label,
          position: { x, y },
          style: {
            border: `2px solid ${skill.color}`,
            color: '#000',
            padding: '16px',
            width: '150px',
            borderRadius: '10px',
            'text-align': 'center',
          },
          data: skill,
        }
      })

      const edges = rawEdges.map((e, i) => ({
        id: `e${i}`,
        source: e.source,
        target: e.target,
        animated: false,
        style: { stroke: '#999' },
      }))

      return { nodes, edges }
    },
    onNodeClick({ node }) {
      this.selectedSkill = node.data;

      // Animate only connected edges
      this.edges = this.edges.map(edge => ({
        ...edge,
        animated:
          edge.source === node.id || edge.target === node.id,
        style: {
          stroke:
            edge.source === node.id || edge.target === node.id
              ? node.data.color
              : '#999',
        },
      }))
    },
    closePanel() {
      this.selectedSkill = null

      // Reset edges
      this.edges = this.edges.map(edge => ({
        ...edge,
        animated: false,
        style: { stroke: '#999' },
      }))
    },
  },
}
</script>

<style>
.skill-map {
  height: 80vh;
}

/* Node styling */
.vue-flow__node-default {
  background: #fefefe;
  border: 2px solid #333;
  border-radius: 8px;
  padding: 16px 24px;
  font-size: 14px;
}

.vue-flow__node-label {
  color: #000;
  font-weight: 600;
}
.vue-flow__container {
  display: flex;
  height: 80vh;
  border-radius: 12px;
  overflow: hidden;
  /* background: #f9fafb; */
}
.vue-flow__node-label {
  color: #000;
}
.skill-map-container {
  display: flex;
  height: 600px;
}

.vue-flow {
  flex: 1;
}

.skill-map-layout {
  display: flex;
  height: 600px;
  width: 100%;
}

/* Vue Flow container */
.flow-wrapper {
  flex: 1;
  position: relative;
}

/* Details panel */
.details-panel {
  width: 320px;
  padding: 16px;
  color: white;
  /* background: #f9f9f9; */
  /* border-left: 2px solid #ddd; */

  transform: translateX(100%);
  transition: transform 0.3s ease;
}

/* Slide in */
.details-panel.open {
  transform: translateX(0);
}

.details-panel h3 {
  margin-top: 0;
}

.details-panel.mobile {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 40vh;
  border-radius: 16px 16px 0 0;
}

.vue-flow__controls-button {
  color: black;
}

h1 {
  color: #00eeff;
  font-size: 2.5rem;
}
h3 {
  color: rgba(255, 255, 255, 0.75);
  font-weight: 200;
}
@media (max-device-width: 660px) {
  #home__content {
    margin-left: 0px !important;
  }
  .flow-wrapper, .vue-flow__container, .vue-flow__transformationpane {
    width: 100vw;
  }
  .skills-description {
    padding: 10px;
  }
}
</style>
